import React, { useCallback, useEffect, useRef, useState } from 'react';

import './SettingsTableOfContents.scss';

export interface TocItem {
	id: string;
	title: string;
}

interface SettingsTableOfContentsProps {
	items: TocItem[];
	/** Offset in px from the top where sticky position starts (default: 64 for tabbed pages) */
	stickyTop?: number;
}

/**
 * A lightweight sticky table of contents for settings pages.
 * Uses IntersectionObserver to highlight the active section and smooth-scrolls on click.
 */
export function SettingsTableOfContents({ items, stickyTop = 64 }: SettingsTableOfContentsProps) {
	const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '');
	const [visibleItems, setVisibleItems] = useState<TocItem[]>(items);
	const observerRef = useRef<IntersectionObserver | null>(null);
	const isClickScrolling = useRef(false);
	const clickTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	// Track which sections are currently intersecting
	const visibleSections = useRef<Map<string, number>>(new Map());

	useEffect(() => {
		// Filter to only items whose DOM element actually exists
		// (conditionally rendered sections may not be in the DOM)
		const present = items.filter((item) => {
			const exists = document.getElementById(item.id);
			if (!exists && import.meta.env.DEV) {
				console.warn(
					`[SettingsTableOfContents] TOC item "${item.title}" has id "${item.id}" but no matching DOM element was found. ` +
					`SettingsSection auto-generates ids via slugify(title). Use createTocItem(title) to keep them in sync.`,
				);
			}
			return exists;
		});
		setVisibleItems(present);
		if (present.length > 0) {
			setActiveId((prev) => present.some((p) => p.id === prev) ? prev : present[0].id);
		}

		// Find the scroll container — .settings-sub-page is the overflow: auto element
		const scrollContainer = document.querySelector('.settings-sub-page');
		if (!scrollContainer) return;

		observerRef.current = new IntersectionObserver(
			(entries) => {
				// Don't update during programmatic scroll
				if (isClickScrolling.current) return;

				for (const entry of entries) {
					const id = entry.target.id;
					if (entry.isIntersecting) {
						visibleSections.current.set(id, entry.intersectionRatio);
					} else {
						visibleSections.current.delete(id);
					}
				}

				// Pick the first visible section in document order
				for (const item of present) {
					if (visibleSections.current.has(item.id)) {
						setActiveId(item.id);
						break;
					}
				}
			},
			{
				root: scrollContainer,
				// Negative top margin accounts for sticky tabs + header offset
				rootMargin: `-${stickyTop + 16}px 0px -40% 0px`,
				threshold: 0,
			},
		);

		// Observe all sections
		for (const item of present) {
			const el = document.getElementById(item.id);
			if (el) observerRef.current.observe(el);
		}

		return () => {
			observerRef.current?.disconnect();
		};
	}, [items, stickyTop]);

	const handleClick = useCallback((e: React.MouseEvent, id: string) => {
		e.preventDefault();
		const el = document.getElementById(id);
		if (!el) return;

		// Flag that we're doing a programmatic scroll
		isClickScrolling.current = true;
		setActiveId(id);

		// Scroll the section into view within the scroll container
		el.scrollIntoView({ behavior: 'smooth', block: 'start' });

		// Clear the flag after scroll settles
		if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
		clickTimeoutRef.current = setTimeout(() => {
			isClickScrolling.current = false;
		}, 800);
	}, []);

	if (visibleItems.length === 0) return null;

	return (
		<nav
			className='settings-toc'
			style={{ top: stickyTop } as React.CSSProperties}
			aria-label='Table of contents'>
			<ul className='settings-toc__list'>
				{visibleItems.map((item) => (
					<li key={item.id} className='settings-toc__item'>
						<a
							href={`#${item.id}`}
							className={`settings-toc__link${activeId === item.id ? ' settings-toc__link--active' : ''}`}
							onClick={(e) => handleClick(e, item.id)}>
							{item.title}
						</a>
					</li>
				))}
			</ul>
		</nav>
	);
}

interface SettingsPageContentProps {
	children: React.ReactNode;
	tocItems: TocItem[];
	/** Offset in px from the top where sticky position starts */
	stickyTop?: number;
}

/**
 * Wrapper that arranges settings content with a right-side table of contents.
 * The TOC is hidden on viewports narrower than 1280px.
 */
export function SettingsPageContent({ children, tocItems, stickyTop }: SettingsPageContentProps) {
	return (
		<div className='settings-page-content'>
			<div className='settings-page-content__main'>{children}</div>
			<SettingsTableOfContents items={tocItems} stickyTop={stickyTop} />
		</div>
	);
}
